import type { FunnelFlow } from '../../msfCheckoutFunnel/interfaces/gateway';

export interface BaseCart {
  id: string; // Legacy retrocompatibility, will be removed with CORT
  items?: CartItemWithProduct[] | null;
  overview?: CartOverview;
  containUnavailableProducts?: boolean;
  delivery?: Delivery;
}

export type CartPlatform = 'FR' | 'ES' | 'IT' | 'DE' | 'GB';

export interface Delivery {
  address: { postalCode: string; city: string; countryIsoCode: string };
}

export type CartOverview = Record<string, number>;

export interface BaseOverview {
  overview: CartOverview;
  id: number;
}

export interface CartLight {
  platform: CartPlatform;
  main: BaseOverview;
}

export interface AddToCartResponseCart {
  platform: CartPlatform;
  main: {
    overview: CartOverview;
    id: number;
    productsTotalPrice: number;
  };
}

export interface PreSalesContact {
  phoneCall?: PreSalesContactPhone;
  availabilities?: PreSalesContactAvailabilities;
}

export interface PreSalesContactPhone {
  phoneNumber: string;
  price: number;
}

export interface PreSalesContactAvailabilities {
  monday: PreSalesContactAvailabilitySlot[];
  tuesday: PreSalesContactAvailabilitySlot[];
  wednesday: PreSalesContactAvailabilitySlot[];
  thursday: PreSalesContactAvailabilitySlot[];
  friday: PreSalesContactAvailabilitySlot[];
  saturday: PreSalesContactAvailabilitySlot[];
  sunday: PreSalesContactAvailabilitySlot[];
}

export interface PreSalesContactAvailabilitySlot {
  start: string;
  end: string;
}

export type CartDetailedItem =
  | CartItemWithProduct
  | CartItemWithService
  | CartItemWithCoupon;

export interface CartItemWithProduct {
  quantity: CartDetailedItemQuantity;
  product: CartProduct;
  service?: CartService;
  coupon?: CartCoupon;
}

export interface CartItemWithService {
  quantity: CartDetailedItemQuantity;
  product?: CartProduct;
  service: CartService;
  coupon?: CartCoupon;
}

export interface CartItemWithCoupon {
  quantity: CartDetailedItemQuantity;
  product?: CartProduct;
  service?: CartService;
  coupon: CartCoupon;
}

export interface CartDetailedItemQuantity {
  value: number;
  increment: number;
  minimum: number;
  maximum?: number;
}

export interface CartProduct {
  id: string;
  name: string;
  modelId: string;
  categoryId?: string;
  picture: string;
  reference: string;
  isSample: boolean;
  price: CartProductPrice;
  article: CartProductArticle;
  brand: CartProductBrand;
  sku: CartProductSku;
  isPro: boolean;
  haveProConditions: boolean;
  isMmf: boolean;
  seller: CartProductSeller;
  stock: number;
  enrichements?: CartProductEnrichements;
  isSolarPanelProduct?: boolean;
}

export interface CartProductPrice {
  currency: string;
  inclVat: number;
  exclVat: number;
  discountedInclVat: number;
  discountedExclVat: number;
}

export interface CartProductArticle {
  id: string;
  name: string;
}

export interface CartProductBrand {
  id: string;
  name: string;
}

export interface CartProductSku {
  manufacturer: string;
  slugArticle: string;
}

export interface CartProductSeller {
  id: string;
  name: string;
  isForeigner: boolean;
}

export interface CartProductEnrichements {
  isCompetitive: boolean;
  commercialOperationids: number[];
}

export interface CartService {
  id: string;
}

export interface CartCoupon {
  id: string;
  amount: string;
}

export enum Strategies {
  RELATIVE = 'relative',
  ABSOLUTE = 'absolute',
}
export interface CartRequestBody {
  value: number;
  strategy: Strategies.ABSOLUTE | Strategies.RELATIVE;
}

export interface AddToCartResponseV2 {
  flow: FunnelFlow;
  cart: AddToCartResponseCart;
  errorNotAdded?: number[];
}

export enum ItemTypes {
  PRODUCT = 'product',
  COUPON = 'coupon',
  SERVICE = 'service',
}

export type CartItemType =
  | ItemTypes.COUPON
  | ItemTypes.PRODUCT
  | ItemTypes.SERVICE;
